import { layers, g } from '@hcie/core';
import { EventBus } from '@hcie/shared';

/**
 * Smart Layer Naming Service (#1325)
 * Automatically names new layers based on existing patterns in the document.
 */

export function initSmartLayerNaming() {
    EventBus.on('LAYER_ADDED', (payload: { layerId: number }) => {
        const newLayer = layers[payload.layerId];
        if (!newLayer) return;

        // Determine if it's a new layer needing an automatic name
        // We look for "Layer", "Vector", or "Untitled" defaults
        const defaultNames = ['Layer', 'Vector', 'Untitled', 'Background'];
        const isDefault = defaultNames.some(dn => newLayer.name.startsWith(dn));
        
        if (!isDefault && newLayer.name !== '') {
            // If it already has a specific name (e.g. from a PSD import), we don't touch it
            // unless it's a generic "Layer X" that we want to standardize.
            if (!newLayer.name.match(/^(Layer|Vector|Untitled)\s*\d*$/i)) {
                return;
            }
        }

        const prefix = newLayer.type === 'vector' ? 'Vector' : 'Layer';
        const newName = generateNextName(prefix);
        
        console.log(`[LayerNaming] Renaming new layer ${payload.layerId} from "${newLayer.name}" to "${newName}"`);
        newLayer.name = newName;
        
        // Refresh UI
        EventBus.emit('UPDATE_LAYER_PANEL');
    });
}

/**
 * Generates the next sequential name for a given prefix.
 * Handles "Layer 1" -> "Layer 2" and "Vector 010" -> "Vector 011" patterns.
 */
function generateNextName(prefix: string): string {
    let maxNum = 0;
    let formatPadding = 0;
    
    const regex = new RegExp(`^${prefix}\\s*(\\d+)$`, 'i');

    for (const layer of layers) {
        const match = layer.name.match(regex);
        if (match) {
            const numStr = match[1];
            const num = parseInt(numStr, 10);
            if (num > maxNum) {
                maxNum = num;
                formatPadding = numStr.startsWith('0') ? numStr.length : 0;
            }
        }
    }

    const nextNum = maxNum + 1;
    let nextNumStr = nextNum.toString();
    
    if (formatPadding > 0) {
        nextNumStr = nextNumStr.padStart(formatPadding, '0');
    }

    return `${prefix} ${nextNumStr}`;
}
